/**
 * Place-bet sheet — the stake selector for an edge call.
 *
 * Sharp8 doesn't take bets. We record the pick in our DB so the user
 * has a track record + the leaderboard has something to score. The
 * actual transaction (if any) happens on the cited bookmaker.
 *
 * UX (Robinhood-style stake picker):
 *   • 4 stake chips: $10 / $25 / $50 / $100
 *   • Custom amount input
 *   • Live "potential win" preview based on price × stake
 *   • Confirm button → POST /api/v1/me/picks
 *   • On success: toast "Pick saved · added to your track record"
 *   • Auto-closes 1.2s after toast appears
 */

import { useState } from 'react';
import type { EdgeCall, UserPick } from '@sharp8/types';
import { haptic, tg } from '../lib/telegram';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

interface Props {
  edge: EdgeCall;
  matchLabel: string;
  open: boolean;
  onClose(): void;
  /** Fires on a successful pick placement with the user's new bankroll
   *  (returned by /api/v1/me/picks). Lets the parent refresh the
   *  BankrollPill / BalanceHero without a /me round-trip. */
  onPlaced?(pick: UserPick, newBankroll: number): void;
  /** Current bankroll, for the in-sheet "you'll have $X left" preview
   *  and the insufficient-funds error message. */
  bankroll?: number;
  /** Picks the user has already placed on this match. When non-empty
   *  the sheet shows a warning banner so the user doesn't accidentally
   *  stack bets they didn't realise they already had. */
  existingPicks?: UserPick[];
}

const STAKE_CHIPS = [10, 25, 50, 100];
const DEFAULT_STAKE = 50;

const FRIENDLY_ERROR: Record<string, string> = {
  insufficient_bankroll: "Not enough game balance to cover this stake. Lower the amount or wait for a settled win to top up.",
  invalid_pick:          "Pick details look off. Try refreshing the match.",
  invalid_stake:         "Stake must be positive.",
  invalid_price:         "Price looks wrong. Refresh the match and try again.",
  match_not_found:       "This match isn't in the database. Try refreshing.",
  unauthorized:          "Open the app from Telegram to place picks.",
};

export function PlaceBetSheet({ edge, matchLabel, open, onClose, onPlaced, bankroll, existingPicks }: Props) {
  const [stake, setStake] = useState<number>(DEFAULT_STAKE);
  const [customMode, setCustomMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<UserPick | null>(null);
  const [doneBankroll, setDoneBankroll] = useState<number | null>(null);

  if (!open) return null;

  const potentialWin = +(stake * (edge.price - 1)).toFixed(2);
  const totalReturn  = +(stake * edge.price).toFixed(2);
  const wouldOverdraw = bankroll != null && stake > bankroll;

  async function place() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    haptic('medium');

    try {
      const initData = tg()?.initData ?? '';
      const res = await fetch(`${API_BASE.replace(/\/$/, '')}/me/picks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(initData ? { 'X-Telegram-Init-Data': initData } : {}),
        },
        body: JSON.stringify({
          matchId: edge.matchId,
          market: edge.market,
          line: edge.line,
          price: edge.price,
          stake,
        }),
      });

      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        const code = String(j.error ?? `place failed: ${res.status}`);
        throw new Error(code);
      }

      const j = await res.json() as { pick: UserPick; bankroll?: number };
      setDone(j.pick);
      if (typeof j.bankroll === 'number') {
        setDoneBankroll(j.bankroll);
        onPlaced?.(j.pick, j.bankroll);
      }
      haptic('medium');

      // Auto-close after celebrating
      setTimeout(() => {
        onClose();
        // Reset state for next open
        setTimeout(() => {
          setDone(null);
          setDoneBankroll(null);
          setStake(DEFAULT_STAKE);
          setCustomMode(false);
        }, 300);
      }, 1800);
    } catch (e: any) {
      const code = String(e?.message ?? e);
      setError(FRIENDLY_ERROR[code] ?? code);
      haptic('light');
      setSubmitting(false);
    }
  }

  const hasExisting = (existingPicks?.length ?? 0) > 0;

  return (
    <>
      <div className="bet-backdrop" onClick={() => !submitting && onClose()} />
      <aside className="bet-sheet" role="dialog" aria-label="Place bet">
        <header className="bet-sheet__header">
          <div className="bet-sheet__handle" />
          {!submitting && (
            <button
              type="button"
              className="bet-sheet__close"
              onClick={onClose}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          )}
          {done ? (
            <>
              <div className="bet-sheet__eyebrow done">Pick saved</div>
              <div className="bet-sheet__match">Tracking in your record</div>
            </>
          ) : (
            <>
              <div className="bet-sheet__eyebrow">Place bet</div>
              <div className="bet-sheet__match">{matchLabel}</div>
            </>
          )}
        </header>

        {done ? (
          <SuccessBody pick={done} newBankroll={doneBankroll} />
        ) : (
          <>
            <div className="bet-sheet__body">
              {hasExisting && (
                <ExistingPickWarning picks={existingPicks!} />
              )}
              <div className="bet-sheet__call">
                <div className="bet-sheet__line">
                  Take <b>{edge.line}</b>
                </div>
                <div className="bet-sheet__price-row">
                  <span className="bet-sheet__price">{edge.price.toFixed(2)}</span>
                  <span className="bet-sheet__book">· {edge.book}</span>
                </div>
              </div>

              <div className="bet-sheet__stake-label">Stake</div>
              <div className="bet-sheet__chips">
                {STAKE_CHIPS.map((n) => (
                  <button
                    key={n}
                    className={`bet-sheet__chip${!customMode && stake === n ? ' is-active' : ''}`}
                    onClick={() => { setStake(n); setCustomMode(false); haptic('light'); }}
                    disabled={submitting}
                  >
                    ${n}
                  </button>
                ))}
                <button
                  className={`bet-sheet__chip${customMode ? ' is-active' : ''}`}
                  onClick={() => { setCustomMode(true); haptic('light'); }}
                  disabled={submitting}
                >
                  Custom
                </button>
              </div>
              {customMode && (
                <div className="bet-sheet__custom">
                  <span className="bet-sheet__dollar">$</span>
                  <input
                    type="number"
                    min={1}
                    max={1000000}
                    step={1}
                    value={stake}
                    onChange={(e) => setStake(Math.max(1, Number(e.target.value) || 0))}
                    autoFocus
                    disabled={submitting}
                  />
                </div>
              )}

              <div className="bet-sheet__projection">
                <div className="bet-sheet__projection-row">
                  <span>Potential win</span>
                  <b className="green">+${potentialWin.toFixed(2)}</b>
                </div>
                <div className="bet-sheet__projection-row">
                  <span>Total return</span>
                  <b>${totalReturn.toFixed(2)}</b>
                </div>
                {bankroll != null && (
                  <div className="bet-sheet__projection-row">
                    <span>Balance after stake</span>
                    <b className={wouldOverdraw ? 'red' : undefined}>
                      ${Math.max(0, bankroll - stake).toFixed(2)}
                    </b>
                  </div>
                )}
              </div>

              {error && (
                <div className="bet-sheet__error">{error}</div>
              )}
            </div>

            <div className="bet-sheet__actions">
              <button
                className="bet-sheet__confirm"
                onClick={place}
                disabled={submitting || stake <= 0 || wouldOverdraw}
              >
                {submitting
                  ? 'Saving…'
                  : wouldOverdraw
                  ? `Need $${(stake - (bankroll ?? 0)).toFixed(2)} more`
                  : `Confirm · $${stake.toFixed(2)} → $${totalReturn.toFixed(2)}`}
              </button>
              <p className="bet-sheet__note">
                Sharp8 is a game · $1,000 starting bankroll. Stake is deducted now;
                payout credits when the match settles.
              </p>
            </div>
          </>
        )}
      </aside>
    </>
  );
}

function ExistingPickWarning({ picks }: { picks: UserPick[] }) {
  return (
    <div className="bet-sheet__existing" role="status">
      <div className="bet-sheet__existing-head">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v5M12 16v.01" />
        </svg>
        <span>
          You've already placed {picks.length === 1 ? 'a bet' : `${picks.length} bets`} on this match
        </span>
      </div>
      <ul className="bet-sheet__existing-list">
        {picks.map((p) => {
          let status: string;
          if      (p.status === 'won'    && p.payout !== null) status = `won +$${(p.payout - p.stake).toFixed(2)}`;
          else if (p.status === 'lost')                        status = `lost −$${p.stake.toFixed(2)}`;
          else if (p.status === 'pushed')                      status = 'pushed';
          else if (p.status === 'voided')                      status = 'voided';
          else                                                 status = 'pending';
          return (
            <li key={p.id} className="bet-sheet__existing-row">
              <span className="bet-sheet__existing-line">{p.line}</span>
              <span className="bet-sheet__existing-meta">
                ${p.stake.toFixed(2)} @ {p.price.toFixed(2)} · {status}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SuccessBody({ pick, newBankroll }: { pick: UserPick; newBankroll: number | null }) {
  return (
    <div className="bet-sheet__success">
      <div className="bet-sheet__success-icon">
        <svg viewBox="0 0 48 48" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="24" cy="24" r="20" />
          <path d="M14 24l7 7 13-14" />
        </svg>
      </div>
      <div className="bet-sheet__success-line">{pick.line}</div>
      <div className="bet-sheet__success-meta">
        ${pick.stake.toFixed(2)} @ {pick.price.toFixed(2)} · pending settlement
      </div>
      {newBankroll != null && (
        <div className="bet-sheet__success-balance">
          Balance · ${newBankroll.toFixed(2)}
        </div>
      )}
    </div>
  );
}
